// ** Polyfills
import ResizeObserver from "resize-observer-polyfill";

// ** React Imports
import { useEffect, useState } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useLayer, Arrow } from "react-laag";
import styled, { css } from "styled-components";

// ** Utils & Helpers
import { publish, subscribe, unsubscribe } from "helpers/events";

// ** Styled Component
import { StyledTooltipBox } from "components/layout/Tooltip/styles/TooltipBox.styled";
import Text from "components/typography/Text";
import Block from "components/layout/Block";
import Toggle from "components/layout/Toggle";

const StyledBlock = styled(Block)`
	display: flex;
	justify-content: space-between;
	max-width: 100%;
	width: 100%;
	align-items: flex-start;
	padding: 0.5rem 1rem;
	border-radius: ${({ theme: { borderRadius } }) => borderRadius.element};
	user-select: none;
	margin-bottom: ${({ $noMarginBottom }) =>
		$noMarginBottom ? "0" : "0.25rem"};

	${({ disabled }) =>
		disabled
			? css`
					cursor: no-drop;
					& ${Text} {
						opacity: var(--dz-sys-button-disabled-opacity);
					}
				`
			: css`
					cursor: pointer;
				`}
	&:hover {
		background-color: ${({ theme: { colors } }) => colors.itemsHover};
	}
`;

export const TogglePreferences = ({
	toggled,
	disabled = false,
	dispatchAction,
	text,
	tooltipText,
	noMarginBottom,
}) => {
	const isDarkMode = useSelector(
		(state) => state.globalCommon?.darkMode?.isDarkMode,
	);
	const [isShow, setShow] = useState(false);

	const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
		onParentClose: () => setShow(false),
		onOutsideClick: () => {
			setShow(false);
		},
		isOpen: isShow,
		auto: true,
		overflowContainer: true,
		placement: "top-center",
		triggerOffset: 12,
		container: "app",
		ResizeObserver,
	});

	useEffect(() => {
		subscribe("closeTooltips", () => {
			setShow(false);
		});

		subscribe("scroll", () => {
			setShow(false);
		});

		return () => {
			unsubscribe("closeTooltips");
			unsubscribe("scroll");
		};
	}, []);

	if (
		(tooltipText && tooltipText.length > 0) ||
		typeof tooltipText === "object"
	) {
		return (
			<StyledBlock
				onClick={() => (disabled ? null : dispatchAction())}
				disabled={disabled}
				// Tooltip props
				ref={triggerProps.ref}
				onPointerEnter={() => {
					setShow(true);
				}}
				onPointerLeave={() => {
					publish("closeTooltips");
				}}
				onTouchStart={() => {
					publish("closeTooltips");
				}}
				onTouchEnd={() => {
					setShow(true);
				}}
				onTouchMove={() => {
					publish("closeTooltips");
				}}
				$noMarginBottom={noMarginBottom}
			>
				<Text>{text}</Text>
				<Block flex ml={2} style={{ height: "21px" }} alignCenter>
					<Toggle toggled={toggled} disabled={disabled} />
				</Block>

				{renderLayer(
					isShow ? (
						<StyledTooltipBox
							className="tooltip-box"
							{...layerProps}
						>
							{tooltipText}
							<Arrow
								{...arrowProps}
								backgroundColor={
									isDarkMode ? "#f2f2f2" : "#000"
								}
								size={6}
							/>
						</StyledTooltipBox>
					) : null,
				)}
			</StyledBlock>
		);
	} else {
		return (
			<StyledBlock
				onClick={() => (disabled ? null : dispatchAction())}
				disabled={disabled}
				$noMarginBottom={noMarginBottom}
			>
				<Text>{text}</Text>
				<Block flex ml={2} style={{ height: "21px" }} alignCenter>
					<Toggle toggled={toggled} disabled={disabled} />
				</Block>
			</StyledBlock>
		);
	}
};
